import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, Typography, Alert, Divider, message } from 'antd';
import {
  lookupMainMeterBySubMeter,
  mqttPublishByMeter,
  getMqttMappingByMeter,
  getMqttLatestMessage,
  rebootRelatedMains,
} from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  buildRemoteRebootHex,
  getRxAddressCandidates,
  rxTopicsForHttpPoll,
  yjRemoteMqttRxWaitMsByMappingTier,
  buildMqttMessagePollOpts,
} from '../utils/yjMeterRemote';
import { createMqttWS } from '../utils/mqttWS';

const { Text, Paragraph } = Typography;

const isMainMeter = (device) => Number(device?.device_detail_type) === 1;

const meterNoOf = (device) =>
  String(device?.current_meter_no || device?.factory_meter_no || '').trim();

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** 仅本弹窗用：解析 52 重启回包 ACK（与 test4 一致：00=成功，01=失败） */
function parseReboot52Ack(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 18 || msg.substring(12, 14) !== '52') return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  const ackHex = msg.substring(16, 18);
  const ackDec = parseInt(ackHex, 16);
  if (!Number.isFinite(ackDec)) return null;
  return { ok: ackDec === 0, ackDec, ackHex };
}

/**
 * 重启弹窗：
 * - 重启对应主表（仅副表）：新接口 reboot-related-mains
 * - 重启当前电表：旧 publish-by-meter 本机 52（与线上小程序兼容）
 */
const MeterRebootModal = ({ open, device, onClose }) => {
  const [selfLoading, setSelfLoading] = useState(false);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [rebootHint, setRebootHint] = useState('');
  const [rebootError, setRebootError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [mainMeterNos, setMainMeterNos] = useState([]);
  const cancelledRef = useRef(false);

  const selfIsMain = isMainMeter(device);
  const busy = selfLoading || relatedLoading;

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      return;
    }
    cancelledRef.current = false;
    setSelfLoading(false);
    setRelatedLoading(false);
    setRebootHint('');
    setRebootError('');
    setLookupLoading(false);
    setLookupError('');
    if (isMainMeter(device)) {
      const no = meterNoOf(device);
      setMainMeterNos(no ? [no] : []);
    } else {
      setMainMeterNos([]);
    }
  }, [open, device?.id]);

  const waitRebootAck = async (deviceRow, publishKey, publishAt) => {
    const mapRes = await getMqttMappingByMeter(publishKey);
    if (!mapRes?.success || !mapRes?.data?.rx_topic) {
      return { kind: 'no_mapping', msg: mapRes?.msg || '未找到 MQTT 主题映射，无法等待表端回执' };
    }
    const mapData = mapRes.data;
    const tier = Number(mapData.mapping_fanout_tier) === 2 ? 2 : 1;
    const waitMs = yjRemoteMqttRxWaitMsByMappingTier(tier);
    const rxTopics = rxTopicsForHttpPoll(mapData);
    const candidates = getRxAddressCandidates(deviceRow);
    const pollOpts = buildMqttMessagePollOpts(publishKey, 'reboot', publishAt);

    let fromWs = null;
    let wsHandle = null;
    if (mapData.rx_topic) {
      const extraRx = tier === 2 ? rxTopics.filter((t) => t !== mapData.rx_topic) : [];
      wsHandle = createMqttWS(
        mapData.rx_topic,
        (_topic, msg, hex) => {
          const p = parseReboot52Ack(String(hex || msg || ''), candidates);
          if (p) fromWs = p;
        },
        { extraRxTopics: extraRx }
      );
      try {
        await Promise.race([wsHandle.ready, sleep(3000)]);
      } catch (_) {
        wsHandle = null;
      }
    }

    const deadline = Date.now() + waitMs;
    let lastRaw = '';
    try {
      while (Date.now() < deadline) {
        if (cancelledRef.current) return { kind: 'cancelled' };
        if (fromWs) {
          return { kind: 'ack', ...fromWs };
        }
        for (let i = 0; i < rxTopics.length; i++) {
          try {
            const msgRes = await getMqttLatestMessage(rxTopics[i], 'raw', pollOpts);
            const raw = String(msgRes?.data?.message ?? msgRes?.data?.hex ?? '').trim();
            if (!raw || raw === lastRaw) continue;
            lastRaw = raw;
            const p = parseReboot52Ack(raw, candidates);
            if (p) return { kind: 'ack', ...p };
          } catch (_) {
            /* ignore */
          }
        }
        await sleep(400);
      }
    } finally {
      try {
        wsHandle?.close();
      } catch (_) {}
    }
    return { kind: 'timeout', waitMs };
  };

  /** 重启当前电表：旧路径本机 52 */
  const handleRebootSelf = async () => {
    if (!device) return;
    setSelfLoading(true);
    setRebootHint('重启中');
    setRebootError('');
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC');
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const payload = buildFinalPayload(buildRemoteRebootHex(publishKey, isBleDirect), true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      if (cancelledRef.current) return;
      setRebootHint('重启中');

      const wait = await waitRebootAck(device, publishKey, publishAt);
      if (cancelledRef.current || wait.kind === 'cancelled') return;

      if (wait.kind === 'ack' && wait.ok) {
        setRebootHint('重启成功');
        setRebootError('');
        message.success('重启成功');
        return;
      }
      if (wait.kind === 'no_mapping') {
        // 已下发但无法等回执：按成功提示（与「点了就重启」一致）
        setRebootHint('重启成功');
        setRebootError('');
        message.success('重启成功');
        return;
      }
      setRebootHint('');
      setRebootError('重启失败');
      message.error('重启失败');
    } catch (e) {
      setRebootHint('');
      setRebootError('重启失败');
      message.error('重启失败');
    } finally {
      if (!cancelledRef.current) setSelfLoading(false);
    }
  };

  /** 重启对应主表：新接口；无主表时后端回退本机 */
  const handleRebootRelated = async () => {
    if (!device?.id) return;
    setRelatedLoading(true);
    setRebootHint('重启中');
    setRebootError('');
    try {
      const res = await rebootRelatedMains({ device_id: device.id });
      if (cancelledRef.current) return;
      const data = res?.data || {};
      const targets = Array.isArray(data.targets) ? data.targets : [];
      const nos = targets
        .map((t) => String(t.current_meter_no || t.publish_key || '').trim())
        .filter(Boolean);
      if (nos.length) setMainMeterNos(nos);

      if (!res?.success) {
        setRebootHint('');
        setRebootError('重启失败');
        message.error('重启失败');
        return;
      }
      setRebootHint('重启成功');
      setRebootError('');
      message.success('重启成功');
    } catch (e) {
      setRebootHint('');
      setRebootError('重启失败');
      message.error('重启失败');
    } finally {
      if (!cancelledRef.current) setRelatedLoading(false);
    }
  };

  const handleLookupMain = async () => {
    if (!device) return;
    const q = meterNoOf(device);
    if (!q) {
      message.warning('当前设备无表号，无法查询主表');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await lookupMainMeterBySubMeter(q);
      if (!res?.success) {
        setLookupError(res?.msg || '查询失败');
        return;
      }
      const list = Array.isArray(res.data?.main_meters) ? res.data.main_meters : [];
      const nos = [];
      list.forEach((m) => {
        const no = String(m?.current_meter_no || m?.factory_meter_no || '').trim();
        if (no && !nos.includes(no)) nos.push(no);
      });
      setMainMeterNos(nos);
      if (!res.data?.latest_rx_topic) {
        setLookupError('未找到该表最近心跳，无法反查主表');
      } else if (!nos.length) {
        setLookupError('已找到 RX 主题，但未匹配到主表');
      } else {
        message.success(`找到 ${nos.length} 个主表表号`);
      }
    } catch (e) {
      setLookupError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLookupLoading(false);
    }
  };

  const meterLabel = meterNoOf(device) || device?.device_mac || device?.id || '';

  return (
    <Modal
      title={`重启${meterLabel ? ` · ${meterLabel}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>重启操作</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            {selfIsMain
              ? '主表：重启当前电表。'
              : '副表：优先重启对应主表（找不到则自动重启本机）；也可点「重启当前电表」只打本机。'}
          </Paragraph>
          <Space wrap>
            {!selfIsMain ? (
              <Button type="primary" danger loading={relatedLoading} disabled={busy} onClick={handleRebootRelated}>
                重启对应主表
              </Button>
            ) : null}
            <Button danger={!selfIsMain} type={selfIsMain ? 'primary' : 'default'} loading={selfLoading} disabled={busy} onClick={handleRebootSelf}>
              重启当前电表
            </Button>
          </Space>
          <div style={{ minHeight: 40, marginTop: 8 }}>
            {rebootError ? (
              <Alert type="error" showIcon message={rebootError} />
            ) : rebootHint ? (
              <Alert type={busy ? 'info' : 'success'} showIcon message={rebootHint} />
            ) : null}
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        <div>
          <Text strong>主表表号</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            {selfIsMain
              ? '当前设备已是 4G 主表，直接显示本表表号。'
              : '可查询或重启后展示目标主表表号（与主表查询页同款反查）。'}
          </Paragraph>

          {selfIsMain ? (
            <Alert
              type="success"
              showIcon
              message={
                mainMeterNos.length
                  ? `主表表号：${mainMeterNos.join('、')}`
                  : '主表档案缺少表号'
              }
            />
          ) : (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Button loading={lookupLoading} disabled={busy} onClick={handleLookupMain}>
                查询主表
              </Button>
              {mainMeterNos.length > 0 ? (
                <Alert
                  type="success"
                  showIcon
                  message={`主表表号：${mainMeterNos.join('、')}`}
                />
              ) : null}
              {lookupError ? <Alert type="error" showIcon message={lookupError} /> : null}
            </Space>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default MeterRebootModal;
